import type { Project, WorkflowColumn } from '@portfolio-pm/shared';
import { parseEstimateNatural, parseNaturalDate, parseRecurrence } from '@portfolio-pm/smart-add';
import { ChevronRight, Edit3, ExternalLink, Plus } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNow } from '../../../lib/useNow.ts';
import {
  formatMinutes,
  getTagColorForDetail,
  startOfDay,
  toDateStr,
  toTimeStr,
} from './detail-utils.ts';

/**
 * Click-to-edit field components for the task detail inspector:
 * calendar and natural-language dates, estimates, URL, recurrence,
 * chip selects, workflow, project search, and tags multi-select.
 * Extracted from TaskDetailPanel.tsx.
 */

export function CalendarDateInput({
  dateId,
  value,
  hasTime,
  onCommit,
}: {
  dateId: string;
  value: number | undefined;
  hasTime: boolean | undefined;
  onCommit(value: number | undefined, hasTime: boolean | undefined): void;
}): JSX.Element {
  const [dateVal, setDateVal] = useState(() => toDateStr(value));
  const [timeVal, setTimeVal] = useState(() => toTimeStr(value));
  const [withTime, setWithTime] = useState(() => !!hasTime);
  // Track the last synced value so we only reset withTime when the date itself changes.
  const syncedValueRef = useRef<number | undefined>(value);

  useEffect(() => {
    const valueChanged = syncedValueRef.current !== value;
    syncedValueRef.current = value;
    setDateVal(toDateStr(value));
    setTimeVal(toTimeStr(value));
    if (valueChanged) {
      setWithTime(!!hasTime);
    }
  }, [value, hasTime]);

  const commit = (d: string, t: string, wt: boolean): void => {
    if (!d) {
      onCommit(undefined, undefined);
      return;
    }
    const dateParts = d.split('-');
    const year = Number(dateParts[0]);
    const month = Number(dateParts[1]);
    const day = Number(dateParts[2]);
    if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return;
    if (!wt) {
      onCommit(new Date(year, month - 1, day).getTime(), false);
      return;
    }
    const timeParts = t.split(':');
    const hour = Number(timeParts[0] ?? '0');
    const minute = Number(timeParts[1] ?? '0');
    if (!Number.isFinite(hour) || !Number.isFinite(minute)) return;
    onCommit(new Date(year, month - 1, day, hour, minute).getTime(), true);
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <input
        id={dateId}
        type="date"
        value={dateVal}
        onChange={(e) => {
          setDateVal(e.target.value);
          commit(e.target.value, timeVal, withTime);
        }}
        className="ui-input"
      />
      <label className="flex cursor-pointer select-none items-center gap-1 text-xs text-[color:var(--app-text-soft)]">
        <input
          type="checkbox"
          checked={withTime}
          onChange={(e) => {
            setWithTime(e.target.checked);
            if (dateVal) commit(dateVal, timeVal, e.target.checked);
          }}
        />
        time
      </label>
      {withTime && (
        <input
          type="time"
          value={timeVal}
          onChange={(e) => {
            setTimeVal(e.target.value);
            commit(dateVal, e.target.value, withTime);
          }}
          className="ui-input"
        />
      )}
    </div>
  );
}

const ESTIMATE_CHIPS = [
  { label: '15 min', minutes: 15 },
  { label: '30 min', minutes: 30 },
  { label: '45 min', minutes: 45 },
  { label: '1 hour', minutes: 60 },
  { label: '2 hours', minutes: 120 },
  { label: '4 hours', minutes: 240 },
  { label: '8 hours', minutes: 480 },
];

export function SmartEstimateField({
  id,
  value,
  onChange,
}: {
  id: string;
  value: number | undefined;
  onChange(minutes: number | undefined): void;
}): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const displayText = value !== undefined ? formatMinutes(value) : undefined;

  const startEdit = (): void => {
    setDraft(value !== undefined ? formatMinutes(value) : '');
    setEditing(true);
    setTimeout(() => inputRef.current?.select(), 0);
  };

  const commit = (): void => {
    const trimmed = draft.trim();
    if (!trimmed || trimmed === 'clear' || trimmed === 'none') {
      onChange(undefined);
    } else {
      const parsed = parseEstimateNatural(trimmed);
      if (parsed !== undefined) onChange(parsed);
    }
    setEditing(false);
  };

  if (editing) {
    return (
      <div className="relative">
        <input
          ref={inputRef}
          id={id}
          // biome-ignore lint/a11y/noAutofocus: this input is conditionally mounted on user action
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commit();
            }
            if (e.key === 'Escape') setEditing(false);
          }}
          placeholder="45 mins, 1 hour 15…"
          className="w-full rounded border border-[color:var(--app-border-strong)] bg-[color:var(--app-input-bg)] px-1 py-0 text-sm text-[color:var(--app-text)] outline-none"
        />
        <div className="absolute left-0 top-full z-50 mt-1 flex w-max min-w-52 max-w-[min(24rem,calc(100vw-2rem))] flex-wrap gap-1 rounded-md border border-[color:var(--app-border)] bg-[color:var(--app-panel)] p-2 shadow-lg">
          {ESTIMATE_CHIPS.map((chip) => (
            <button
              key={chip.label}
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                onChange(chip.minutes);
                setEditing(false);
              }}
              className="inline-flex h-6 max-w-full items-center rounded border border-[color:var(--app-border)] bg-[color:var(--app-control-bg)] px-2 text-[11px] text-[color:var(--app-text-muted)] hover:border-[color:var(--app-border-strong)] hover:text-[color:var(--app-text)]"
            >
              {chip.label}
            </button>
          ))}
          {value !== undefined && (
            <button
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                onChange(undefined);
                setEditing(false);
              }}
              className="inline-flex h-6 max-w-full items-center rounded border border-[color:var(--app-border)] bg-[color:var(--app-control-bg)] px-2 text-[11px] text-[color:var(--app-danger)] hover:border-[color:var(--app-danger)]"
            >
              Clear
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <button
      type="button"
      id={id}
      onClick={startEdit}
      className="rounded border border-transparent px-1 py-0 text-left text-sm text-[color:var(--app-text)] hover:border-[color:var(--app-border)] hover:bg-[color:var(--app-input-bg)]"
    >
      {displayText ?? <span className="text-[color:var(--app-text-soft)]">none</span>}
    </button>
  );
}

const DATE_CHIPS: Array<{ label: string; offset?: number; time?: number }> = [
  { label: 'Today', offset: 0 },
  { label: 'Tomorrow', offset: 1 },
  { label: '+1 week', offset: 7 },
  { label: '+2 weeks', offset: 14 },
];

export function SmartDateField({
  id,
  value,
  hasTime,
  onCommit,
}: {
  id: string;
  value: number | undefined;
  hasTime: boolean | boolean | undefined;
  onCommit(value: number | undefined, hasTime: boolean | undefined): void;
}): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const now = useNow();

  const displayText = value !== undefined ? formatDetailDate(value, hasTime, now) : undefined;

  const startEdit = (): void => {
    setDraft(value !== undefined ? formatDetailDate(value, hasTime) : '');
    setEditing(true);
    setTimeout(() => inputRef.current?.select(), 0);
  };

  const commit = (text = draft): void => {
    const trimmed = text.trim();
    if (!trimmed || trimmed.toLowerCase() === 'none' || trimmed.toLowerCase() === 'clear') {
      onCommit(undefined, undefined);
    } else {
      const parsed = parseNaturalDate(trimmed);
      if (parsed) onCommit(parsed.value, parsed.hasTime);
    }
    setEditing(false);
  };

  const applyChip = (offset: number): void => {
    const d = new Date();
    d.setDate(d.getDate() + offset);
    d.setHours(0, 0, 0, 0);
    onCommit(d.getTime(), false);
    setEditing(false);
  };

  if (editing) {
    return (
      <div className="relative">
        <input
          ref={inputRef}
          id={id}
          // biome-ignore lint/a11y/noAutofocus: this input is conditionally mounted on user action
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => commit()}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commit();
            }
            if (e.key === 'Escape') setEditing(false);
          }}
          placeholder="today at midday, next Friday…"
          className="w-full rounded border border-[color:var(--app-border-strong)] bg-[color:var(--app-input-bg)] px-1 py-0 text-sm text-[color:var(--app-text)] outline-none"
        />
        <div className="absolute left-0 top-full z-50 mt-1 flex flex-wrap gap-1 rounded-md border border-[color:var(--app-border)] bg-[color:var(--app-panel)] p-2 shadow-lg">
          {DATE_CHIPS.map((chip) => (
            <button
              key={chip.label}
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                applyChip(chip.offset ?? 0);
              }}
              className="inline-flex h-6 items-center rounded border border-[color:var(--app-border)] bg-[color:var(--app-control-bg)] px-2 text-[11px] text-[color:var(--app-text-muted)] hover:border-[color:var(--app-border-strong)] hover:text-[color:var(--app-text)]"
            >
              {chip.label}
            </button>
          ))}
          {value !== undefined && (
            <button
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                onCommit(undefined, undefined);
                setEditing(false);
              }}
              className="inline-flex h-6 items-center rounded border border-[color:var(--app-border)] bg-[color:var(--app-control-bg)] px-2 text-[11px] text-[color:var(--app-danger)] hover:border-[color:var(--app-danger)]"
            >
              Clear
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <button
      type="button"
      id={id}
      onClick={startEdit}
      className="rounded border border-transparent px-1 py-0 text-left text-sm text-[color:var(--app-text)] hover:border-[color:var(--app-border)] hover:bg-[color:var(--app-input-bg)]"
    >
      {displayText ?? <span className="text-[color:var(--app-text-soft)]">none</span>}
    </button>
  );
}

const DETAIL_DATE_FORMAT = new Intl.DateTimeFormat(undefined, {
  weekday: 'short',
  day: 'numeric',
  month: 'short',
});

const DETAIL_DATE_WITH_YEAR_FORMAT = new Intl.DateTimeFormat(undefined, {
  weekday: 'short',
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});

const DETAIL_TIME_FORMAT = new Intl.DateTimeFormat(undefined, {
  hour: '2-digit',
  minute: '2-digit',
});

function formatDetailDate(
  value: number,
  hasTime: boolean | undefined,
  now: number = Date.now(),
): string {
  const dayDiff = Math.round((startOfDay(value) - startOfDay(now)) / (24 * 60 * 60 * 1000));
  const sameYear = new Date(value).getFullYear() === new Date(now).getFullYear();
  const dateLabel =
    dayDiff === 0
      ? 'Today'
      : dayDiff === 1
        ? 'Tomorrow'
        : dayDiff === -1
          ? 'Yesterday'
          : (sameYear ? DETAIL_DATE_FORMAT : DETAIL_DATE_WITH_YEAR_FORMAT).format(new Date(value));
  if (!hasTime) return dateLabel;
  return `${dateLabel} at ${DETAIL_TIME_FORMAT.format(new Date(value))}`;
}

export function UrlField({
  id,
  value,
  onChange,
}: {
  id: string;
  value: string | undefined;
  onChange(url: string | undefined): void;
}): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? '');
  const [hovered, setHovered] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setDraft(value ?? '');
  }, [value]);

  const commit = (): void => {
    const trimmed = draft.trim();
    onChange(trimmed || undefined);
    setEditing(false);
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        id={id}
        // biome-ignore lint/a11y/noAutofocus: this input is conditionally mounted on user action
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commit();
          }
          if (e.key === 'Escape') {
            setDraft(value ?? '');
            setEditing(false);
          }
        }}
        placeholder="https://..."
        className="w-full rounded border border-[color:var(--app-border-strong)] bg-[color:var(--app-input-bg)] px-1 py-0 text-sm text-[color:var(--app-text)] outline-none"
      />
    );
  }

  if (value) {
    return (
      <span
        className="flex min-w-0 items-start gap-1"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <a
          id={id}
          href={value}
          target="_blank"
          rel="noopener noreferrer"
          className="min-w-0 break-words text-sm [overflow-wrap:anywhere] text-[color:var(--app-accent-text)] underline"
        >
          <ExternalLink aria-hidden="true" size={11} className="mr-0.5 inline" />
          {value.replace(/^https?:\/\//, '')}
        </a>
        {hovered && (
          <button
            type="button"
            aria-label="Edit URL"
            onClick={() => {
              setDraft(value);
              setEditing(true);
            }}
            className="flex h-5 w-5 shrink-0 items-center justify-center rounded text-[color:var(--app-text-soft)] hover:bg-[color:var(--app-surface-muted)]"
          >
            <Edit3 aria-hidden="true" size={12} />
          </button>
        )}
      </span>
    );
  }

  return (
    <button
      type="button"
      id={id}
      onClick={() => {
        setDraft('');
        setEditing(true);
      }}
      className="rounded border border-transparent px-1 py-0 text-left text-sm text-[color:var(--app-text-soft)] hover:border-[color:var(--app-border)] hover:bg-[color:var(--app-input-bg)]"
    >
      none
    </button>
  );
}

const REPEAT_CHIPS = [
  { label: 'Never', value: null },
  { label: 'Daily', value: 'daily' },
  { label: 'Weekdays', value: 'every weekday' },
  { label: 'Weekly', value: 'weekly' },
  { label: 'Monthly', value: 'monthly' },
  { label: 'Yearly', value: 'yearly' },
] as const;

export function SmartRepeatField({
  value,
  onChange,
}: {
  value: { rrule: string; fromCompletion: boolean } | null;
  onChange(recurrence: { rrule: string; fromCompletion: boolean } | null): void;
}): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const displayText = value ? formatRepeatRule(value, 'label') : 'Never';

  const startEdit = (): void => {
    setDraft(value ? formatRepeatRule(value, 'input') : '');
    setEditing(true);
    setTimeout(() => inputRef.current?.select(), 0);
  };

  const applyText = (text: string): void => {
    const trimmed = text.trim().toLowerCase();
    if (!trimmed || trimmed === 'never' || trimmed === 'none' || trimmed === 'clear') {
      onChange(null);
    } else {
      const parsed = parseRecurrence(trimmed);
      if (parsed) onChange({ rrule: parsed.rrule, fromCompletion: parsed.fromCompletion });
    }
    setEditing(false);
  };

  if (editing) {
    return (
      <div className="relative">
        <input
          ref={inputRef}
          // biome-ignore lint/a11y/noAutofocus: this input is conditionally mounted on user action
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => applyText(draft)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              applyText(draft);
            }
            if (e.key === 'Escape') setEditing(false);
          }}
          placeholder="daily, every 2 weeks, after 6 weeks…"
          className="w-full rounded border border-[color:var(--app-border-strong)] bg-[color:var(--app-input-bg)] px-1 py-0 text-sm text-[color:var(--app-text)] outline-none"
        />
        <div className="absolute left-0 top-full z-50 mt-1 flex flex-wrap gap-1 rounded-md border border-[color:var(--app-border)] bg-[color:var(--app-panel)] p-2 shadow-lg">
          {REPEAT_CHIPS.map((chip) => (
            <button
              key={chip.label}
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                if (chip.value === null) {
                  onChange(null);
                  setEditing(false);
                  return;
                }
                const parsed = parseRecurrence(chip.value);
                if (parsed)
                  onChange({ rrule: parsed.rrule, fromCompletion: parsed.fromCompletion });
                setEditing(false);
              }}
              className="inline-flex h-6 items-center rounded border border-[color:var(--app-border)] bg-[color:var(--app-control-bg)] px-2 text-[11px] text-[color:var(--app-text-muted)] hover:border-[color:var(--app-border-strong)] hover:text-[color:var(--app-text)]"
            >
              {chip.label}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={startEdit}
      className="rounded border border-transparent px-1 py-0 text-left text-sm text-[color:var(--app-text)] hover:border-[color:var(--app-border)] hover:bg-[color:var(--app-input-bg)]"
    >
      {displayText}
    </button>
  );
}

function formatRepeatRule(
  recurrence: { rrule: string; fromCompletion: boolean },
  mode: 'label' | 'input',
): string {
  const parts = parseRepeatRuleParts(recurrence.rrule);
  if (!parts) return mode === 'label' ? 'Repeats (custom)' : recurrence.rrule;
  if (!recurrence.fromCompletion && parts.byday === 'MO,TU,WE,TH,FR') {
    return mode === 'label' ? 'Repeats every weekday' : 'every weekday';
  }

  const unit = unitForFrequency(parts.freq);
  if (!unit) return mode === 'label' ? 'Repeats (custom)' : recurrence.rrule;

  const cadence = recurrence.fromCompletion ? 'after' : 'every';
  const interval = parts.interval;
  const unitText = interval === 1 ? unit : `${unit}s`;
  const text = interval === 1 ? `${cadence} ${unitText}` : `${cadence} ${interval} ${unitText}`;
  return mode === 'label' ? `Repeats ${text}` : text;
}

function parseRepeatRuleParts(rrule: string):
  | {
      freq: string;
      interval: number;
      byday?: string;
    }
  | undefined {
  const parts = new Map<string, string>();
  for (const segment of rrule.split(';')) {
    const [key, value] = segment.split('=');
    if (key && value) parts.set(key.toUpperCase(), value.toUpperCase());
  }
  const freq = parts.get('FREQ');
  if (!freq) return undefined;
  const interval = Number.parseInt(parts.get('INTERVAL') ?? '1', 10);
  if (!Number.isInteger(interval) || interval <= 0) return undefined;
  const byday = parts.get('BYDAY');
  return byday ? { freq, interval, byday } : { freq, interval };
}

function unitForFrequency(freq: string): string | undefined {
  switch (freq) {
    case 'DAILY':
      return 'day';
    case 'WEEKLY':
      return 'week';
    case 'MONTHLY':
      return 'month';
    case 'YEARLY':
      return 'year';
    default:
      return undefined;
  }
}

export function ChipSelectField<T extends string | null>({
  id,
  value,
  options,
  onChange,
}: {
  id: string;
  value: T;
  options: ReadonlyArray<{ value: T; label: string }>;
  onChange(value: T): void;
}): JSX.Element {
  return (
    <div id={id} className="flex flex-wrap gap-1">
      {options.map((opt) => {
        const active = opt.value === value || (opt.value === null && value == null);
        return (
          <button
            key={String(opt.value)}
            type="button"
            onClick={() => onChange(opt.value)}
            className={`inline-flex h-6 items-center rounded border px-2 text-[11px] transition ${
              active
                ? 'border-[color:var(--app-accent)] bg-[color:var(--app-accent-soft)] font-semibold text-[color:var(--app-accent-text)]'
                : 'border-[color:var(--app-border)] bg-[color:var(--app-control-bg)] text-[color:var(--app-text-muted)] hover:border-[color:var(--app-border-strong)] hover:text-[color:var(--app-text)]'
            }`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

export function WorkflowSelectField({
  id,
  columns,
  value,
  onChange,
}: {
  id: string;
  columns: WorkflowColumn[];
  value: string;
  onChange(state: string): void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const current = columns.find((c) => c.id === value);

  const select = (id: string): void => {
    onChange(id);
    setOpen(false);
  };

  return (
    <div className="relative">
      <button
        type="button"
        id={id}
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1 rounded border border-transparent px-1 py-0 text-sm text-[color:var(--app-text)] hover:border-[color:var(--app-border)] hover:bg-[color:var(--app-input-bg)]"
      >
        {current?.name ?? value}
        <ChevronRight
          aria-hidden="true"
          size={11}
          className="rotate-90 text-[color:var(--app-text-soft)]"
        />
      </button>
      {open && (
        <>
          {/* biome-ignore lint/a11y/useKeyWithClickEvents: pointer-only dismiss backdrop; the trigger stays keyboard-operable */}
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-full z-50 mt-1 min-w-[9rem] overflow-hidden rounded-md border border-[color:var(--app-border)] bg-[color:var(--app-panel)] py-1 shadow-md">
            {columns.map((column) => (
              <button
                key={column.id}
                type="button"
                onClick={() => select(column.id)}
                className={`flex w-full items-center px-3 py-1.5 text-left text-sm ${
                  column.id === value
                    ? 'bg-[color:var(--app-accent-soft)] font-medium text-[color:var(--app-accent-text)]'
                    : 'text-[color:var(--app-text)] hover:bg-[color:var(--app-surface-muted)]'
                }`}
              >
                {column.name}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export function ProjectSearchField({
  id,
  projects,
  value,
  onChange,
}: {
  id: string;
  projects: Project[];
  value: string;
  onChange(projectId: string): void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const currentProject = projects.find((p) => p.id === value);

  const filtered = query.trim()
    ? projects.filter((p) => p.name.toLowerCase().includes(query.toLowerCase()))
    : projects;

  const select = (projectId: string): void => {
    onChange(projectId);
    setOpen(false);
    setQuery('');
  };

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 0);
  }, [open]);

  return (
    <div className="relative">
      <button
        type="button"
        id={id}
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1 rounded border border-transparent px-1 py-0 text-sm text-[color:var(--app-text)] hover:border-[color:var(--app-border)] hover:bg-[color:var(--app-input-bg)]"
      >
        {currentProject?.name ?? '—'}
        <ChevronRight
          aria-hidden="true"
          size={11}
          className="rotate-90 text-[color:var(--app-text-soft)]"
        />
      </button>
      {open && (
        <>
          {/* biome-ignore lint/a11y/useKeyWithClickEvents: pointer-only dismiss backdrop; Escape closes via the search input */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => {
              setOpen(false);
              setQuery('');
            }}
          />
          <div className="absolute left-0 top-full z-50 mt-1 w-52 overflow-hidden rounded-md border border-[color:var(--app-border)] bg-[color:var(--app-panel)] shadow-md">
            <div className="border-b border-[color:var(--app-border)] p-1.5">
              <input
                ref={inputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    setOpen(false);
                    setQuery('');
                  }
                  if (e.key === 'Enter') {
                    const only = filtered.length === 1 ? filtered[0] : undefined;
                    if (only) select(only.id);
                  }
                }}
                placeholder="Search projects…"
                className="w-full rounded border border-[color:var(--app-border)] bg-[color:var(--app-input-bg)] px-2 py-1 text-sm outline-none placeholder:text-[color:var(--app-text-soft)] focus:border-[color:var(--app-border-strong)]"
              />
            </div>
            <ul className="max-h-52 overflow-y-auto py-1">
              {filtered.map((project) => (
                <li key={project.id}>
                  <button
                    type="button"
                    onClick={() => select(project.id)}
                    className={`flex w-full items-center px-3 py-1.5 text-left text-sm ${
                      project.id === value
                        ? 'bg-[color:var(--app-accent-soft)] font-medium text-[color:var(--app-accent-text)]'
                        : 'text-[color:var(--app-text)] hover:bg-[color:var(--app-surface-muted)]'
                    }`}
                  >
                    {project.name}
                  </button>
                </li>
              ))}
              {filtered.length === 0 && (
                <li className="px-3 py-2 text-sm text-[color:var(--app-text-soft)]">
                  No projects found
                </li>
              )}
            </ul>
          </div>
        </>
      )}
    </div>
  );
}

export function TagsMultiSelectField({
  id,
  tags,
  allTags,
  onChange,
}: {
  id: string;
  tags: string[];
  allTags: Array<{ tag: string; count: number }>;
  onChange(tags: string[]): void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [pending, setPending] = useState<string[]>(tags);
  const inputRef = useRef<HTMLInputElement>(null);

  // Sync pending when the panel opens
  const openPanel = (): void => {
    setPending([...tags]);
    setQuery('');
    setOpen(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const apply = (): void => {
    onChange(pending);
    setOpen(false);
  };

  const clear = (): void => {
    setPending([]);
  };

  const toggle = (tag: string): void => {
    setPending((prev) => (prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]));
  };

  // Tags to show: all known + any on the task not yet in allTags
  const knownSet = new Set(allTags.map((t) => t.tag));
  const extra = tags.filter((t) => !knownSet.has(t)).map((t) => ({ tag: t, count: 0 }));
  const allOptions = [...extra, ...allTags];

  // If query looks like a new tag not in the list, offer to add it
  const queryTag = query.trim().replace(/^#/, '');
  const filteredRaw = queryTag
    ? allOptions.filter((t) => t.tag.toLowerCase().includes(queryTag.toLowerCase()))
    : allOptions;
  // selected tags float to the top
  const filtered = [
    ...filteredRaw.filter((t) => pending.includes(t.tag)),
    ...filteredRaw.filter((t) => !pending.includes(t.tag)),
  ];
  const canAddNew =
    queryTag !== '' && !allOptions.some((t) => t.tag.toLowerCase() === queryTag.toLowerCase());

  return (
    <div className="relative">
      <button
        type="button"
        id={id}
        onClick={openPanel}
        className="flex w-full min-w-0 flex-wrap items-center gap-1 rounded border border-transparent px-1 py-0.5 text-left hover:border-[color:var(--app-border)] hover:bg-[color:var(--app-input-bg)]"
      >
        {tags.length === 0 ? (
          <span className="text-sm text-[color:var(--app-text-soft)]">none</span>
        ) : (
          tags.map((tag) => (
            <span
              key={tag}
              className="inline-flex h-5 max-w-full items-center overflow-hidden text-ellipsis whitespace-nowrap rounded px-1.5 text-[11px] font-semibold text-white"
              style={{ backgroundColor: getTagColorForDetail(tag) }}
            >
              {tag}
            </span>
          ))
        )}
      </button>

      {open && (
        <>
          {/* biome-ignore lint/a11y/useKeyWithClickEvents: pointer-only dismiss backdrop; Escape applies via the search input */}
          <div className="fixed inset-0 z-40" onClick={apply} />
          <div className="absolute left-0 top-full z-50 mt-1 w-60 overflow-hidden rounded-md border border-[color:var(--app-border)] bg-[color:var(--app-panel)] shadow-md">
            {/* search / add input */}
            <div className="border-b border-[color:var(--app-border)] p-1.5">
              <input
                ref={inputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    apply();
                  }
                  if (e.key === 'Enter') {
                    if (canAddNew) {
                      toggle(queryTag);
                      setQuery('');
                    } else {
                      const only = filtered.length === 1 ? filtered[0] : undefined;
                      if (only) toggle(only.tag);
                    }
                  }
                }}
                placeholder="Search or add tags"
                className="w-full rounded border border-[color:var(--app-border)] bg-[color:var(--app-input-bg)] px-2 py-1 text-sm outline-none placeholder:text-[color:var(--app-text-soft)] focus:border-[color:var(--app-border-strong)]"
              />
            </div>

            {/* tag list */}
            <ul className="max-h-56 overflow-y-auto">
              {canAddNew && (
                <li>
                  <button
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      toggle(queryTag);
                      setQuery('');
                    }}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-[color:var(--app-accent-text)] hover:bg-[color:var(--app-surface-muted)]"
                  >
                    <Plus aria-hidden="true" size={13} />
                    Add &ldquo;{queryTag}&rdquo;
                  </button>
                </li>
              )}
              {filtered.map(({ tag, count }) => {
                const selected = pending.includes(tag);
                return (
                  <li key={tag}>
                    <button
                      type="button"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        toggle(tag);
                      }}
                      className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm ${
                        selected
                          ? 'bg-[color:var(--app-accent-soft)]'
                          : 'hover:bg-[color:var(--app-surface-muted)]'
                      }`}
                    >
                      {/* checkbox */}
                      <span
                        className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                          selected
                            ? 'border-[color:var(--app-accent)] bg-[color:var(--app-accent)]'
                            : 'border-[color:var(--app-border-strong)]'
                        }`}
                      >
                        {selected && (
                          <svg
                            aria-hidden="true"
                            viewBox="0 0 12 12"
                            className="h-2.5 w-2.5 fill-none stroke-white stroke-2"
                          >
                            <polyline points="2,6 5,9 10,3" />
                          </svg>
                        )}
                      </span>
                      {/* coloured tag dot + name */}
                      <span
                        className="h-2.5 w-2.5 shrink-0 rounded-full"
                        style={{ backgroundColor: getTagColorForDetail(tag) }}
                      />
                      <span
                        className={`flex-1 truncate ${selected ? 'font-medium text-[color:var(--app-accent-text)]' : 'text-[color:var(--app-text)]'}`}
                      >
                        #{tag}
                      </span>
                      {count > 0 && (
                        <span className="shrink-0 text-xs text-[color:var(--app-text-soft)]">
                          {count}
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
              {filtered.length === 0 && !canAddNew && (
                <li className="px-3 py-2 text-sm text-[color:var(--app-text-soft)]">
                  No tags found
                </li>
              )}
            </ul>

            {/* footer */}
            <div className="flex justify-end gap-2 border-t border-[color:var(--app-border)] px-3 py-2">
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  clear();
                }}
                className="text-sm text-[color:var(--app-text-soft)] hover:text-[color:var(--app-text)]"
              >
                Clear
              </button>
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  apply();
                }}
                className="rounded bg-[color:var(--app-accent)] px-3 py-1 text-sm font-medium text-white"
              >
                Apply
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
